import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import dateUtil from '../../../utils/dateUtil';
import ShiftService from '../services/ShiftService';
import { HttpError } from '../../../core/services/HttpService';
import globalErrorHandler from '../../../core/services/GlobalErrorHandler';
import shiftErrorHandler from '../services/ShiftErrorHandler';
import LocalStorageService from '../../../core/services/LocalStorageService';
import SwapService, { SwapRequestMinimal } from '../services/SwapService';
import { useAutoAssign } from './useAutoAssign';

interface UseShiftHandlersDeps {
  activeTab: string;
  startDate: Date;
  tableShifts: any[];
  allEmployees: any[];
  workAreaOptions: any[];
  draggableWAList: any[];
  hiddenWorkAreas: number[];
  activeEmployeeId: number | null;
  editShiftData: any;
  dragging: any;
  dropTarget: { date: string; waId: number } | null;
  showEditShift: boolean;
  setShowEditShift: (v: boolean) => void;
  setEditShiftData: (v: any) => void;
  setShiftData: (v: any) => void;
  setShowAddShift: (v: boolean) => void;
  setDragging: (v: any) => void;
  setDropTarget: (v: any) => void;
  setDraggableWAList: (v: any[]) => void;
  setShowClearAllModal: (v: boolean) => void;
  setShowAutoAssignConfirm: (v: boolean) => void;
  setIsAssigning: (v: boolean) => void;
  setAssignmentLogs: (v: any) => void;
  addLog: (msg: string) => void;
  fetchTableShifts: () => Promise<any>;
}

export function useShiftHandlers(deps: UseShiftHandlersDeps) {
  const {
    activeTab, startDate, tableShifts, allEmployees, workAreaOptions,
    draggableWAList, activeEmployeeId, editShiftData,
    dragging, dropTarget,
    setShowEditShift, setEditShiftData, setShiftData, setShowAddShift,
    setDragging, setDropTarget, setDraggableWAList,
    setShowClearAllModal, setShowAutoAssignConfirm, setIsAssigning,
    setAssignmentLogs, addLog, fetchTableShifts,
  } = deps;

  const [swapRequests, setSwapRequests] = useState<SwapRequestMinimal[]>([]);

  // ===== Swap requests =====
  useEffect(() => {
    if (activeTab !== 'shifts') return;
    const loadSwaps = async () => {
      try {
        const sd = new Date(startDate);
        const ed = new Date(sd);
        ed.setDate(ed.getDate() + 6);
        const indicators: any[] = await SwapService.listSwapRequests({
          start_date: sd.toISOString().split('T')[0],
          end_date: ed.toISOString().split('T')[0],
        });
        const requestIds = [...new Set(indicators.map((i: any) => i.swap_request_id).filter(Boolean))];
        const details = await Promise.allSettled(
          requestIds.map((id: any) => SwapService.getSwapRequest(id))
        );
        const findRequesterName = (requesterShiftId: number) => {
          for (const dayObj of tableShifts as any[]) {
            const shift = dayObj?.shifts?.find((s: any) => s.id === requesterShiftId);
            if (shift?.assigned_employees?.length > 0) {
              const emp = shift.assigned_employees[0];
              return `${emp.first_name} ${emp.last_name}`;
            }
          }
          return null;
        };
        const allEntries: any[] = indicators.map((ind: any) => ({
          ...ind,
          requester_name: ind.requester_name || ind.employee_name || findRequesterName(ind.shift_id),
        }));
        details.forEach((result: any) => {
          if (result.status !== 'fulfilled') return;
          const detail = result.value;
          const reqName = findRequesterName(detail.requester_shift?.id);
          detail.options?.forEach((opt: any) => {
            const targetShiftId = opt.target_shift?.id || opt.target_shift_id;
            if (targetShiftId && !allEntries.some((e: any) => e.shift_id === targetShiftId)) {
              allEntries.push({
                shift_id: targetShiftId,
                swap_request_id: detail.id,
                status: opt.status || detail.status,
                is_target: true,
                requester_name: reqName,
              });
            }
          });
        });
        console.log('[WeeklyShifts] Swap indicators + targets:', allEntries.length, allEntries);
        setSwapRequests(allEntries);
      } catch (err) {
        console.warn('[WeeklyShifts] Swap requests fetch failed:', err);
        setSwapRequests([]);
      }
    };
    loadSwaps();
  }, [activeTab, startDate, tableShifts]);

  // ===== Drag handlers =====
  const handleLongPressStart = useCallback(
    (shift: any, dateStr: string) => (ev: any) => {
      const point = ev.touches?.[0] ?? ev;
      setDragging({ shiftId: shift.id, startX: point.clientX, startY: point.clientY, x: point.clientX, y: point.clientY });
      ev.preventDefault?.();
    },
    [setDragging]
  );

  const handleDragMove = useCallback(
    (ev: any) => {
      if (!dragging) return;
      const point = ev.touches?.[0] ?? ev;
      setDragging((prev: any) => (prev ? { ...prev, x: point.clientX, y: point.clientY } : null));
    },
    [dragging, setDragging]
  );

  const handleDragEnd = useCallback(async () => {
    if (!dragging) return;
    const current = dragging;
    setDragging(null);
    if (!dropTarget) return;
    try {
      let selected: any = null;
      outer: for (let dayObj of tableShifts as any[]) {
        for (let s of dayObj.shifts || []) {
          if (s.id === current.shiftId) { selected = s; break outer; }
        }
      }
      if (!selected) return;
      const sDT = dateUtil.convertUTCToTenantTime(selected.start_time) || new Date(selected.start_time);
      const eDT = dateUtil.convertUTCToTenantTime(selected.end_time) || new Date(selected.end_time);
      const pad = (n: number) => String(n).padStart(2, '0');
      const startTimeStr = `${pad(sDT.getHours())}:${pad(sDT.getMinutes())}`;
      const endTimeStr = `${pad(eDT.getHours())}:${pad(eDT.getMinutes())}`;
      let dragStartUtc = dateUtil.createTenantTimeAsUTC(dropTarget.date, startTimeStr);
      let dragEndUtc = dateUtil.createTenantTimeAsUTC(dropTarget.date, endTimeStr);
      if (dragStartUtc && dragEndUtc && dragEndUtc <= dragStartUtc) {
        const nextDay = new Date(dropTarget.date);
        nextDay.setDate(nextDay.getDate() + 1);
        const nextDayStr = `${nextDay.getFullYear()}-${String(nextDay.getMonth() + 1).padStart(2, '0')}-${String(nextDay.getDate()).padStart(2, '0')}`;
        dragEndUtc = dateUtil.createTenantTimeAsUTC(nextDayStr, endTimeStr) || dragEndUtc;
      }
      await ShiftService.updateShift(
        selected.id,
        {
          start_time: dragStartUtc || new Date().toISOString(),
          end_time: dragEndUtc || new Date().toISOString(),
          work_area: dropTarget.waId,
          employees_needed: selected.employees_needed || 1,
          notes: selected.notes || '',
        },
        'this'
      );
      await fetchTableShifts();
    } finally {
      setDropTarget(null);
    }
  }, [dragging, dropTarget, tableShifts, fetchTableShifts, setDragging, setDropTarget]);

  useEffect(() => {
    if (!dragging) return;
    window.addEventListener('mousemove', handleDragMove as any);
    window.addEventListener('mouseup', handleDragEnd as any);
    window.addEventListener('touchmove', handleDragMove as any, { passive: false } as any);
    window.addEventListener('touchend', handleDragEnd as any);
    return () => {
      window.removeEventListener('mousemove', handleDragMove as any);
      window.removeEventListener('mouseup', handleDragEnd as any);
      window.removeEventListener('touchmove', handleDragMove as any);
      window.removeEventListener('touchend', handleDragEnd as any);
    };
  }, [dragging, handleDragMove, handleDragEnd]);

  // ===== Shift assignment handlers =====
  const createAssignmentWithOverlapCheck = async (payload: any, autoRemove: boolean) => {
    const finalPayload = { ...payload };
    if (autoRemove) finalPayload.auto_remove_overlap = true;
    try {
      await ShiftService.createShiftAssignment(finalPayload);
      await fetchTableShifts();
    } catch (error: any) {
      if (error instanceof HttpError && error.status === 409) {
        const msg = error.data?.detail || 'Overlap found. Remove older SHIFT?';
        const yes = window.confirm(msg);
        if (yes) await createAssignmentWithOverlapCheck(payload, true);
      } else {
        if (error instanceof HttpError) {
          if (!shiftErrorHandler.handle(error)) globalErrorHandler.handle(error);
        }
      }
    }
  };

  const handleToggleAssignment = async (shiftId: number, empId: number, isAutoAssign = false) => {
    let foundShift: any = null;
    outer: for (let dayObj of tableShifts as any[]) {
      if (!dayObj.shifts) continue;
      for (let s of dayObj.shifts) {
        if (s.id === shiftId) { foundShift = s; break outer; }
      }
    }
    if (!foundShift) return;

    const emp = allEmployees.find((e: any) => e.id === empId);
    if (emp && foundShift.work_area) {
      const isQualified = emp.work_areas.includes(foundShift.work_area);
      if (!isQualified && !isAutoAssign) {
        const waName = foundShift.work_area__name || 'this WorkArea';
        const confirmMsg = `Employee ${emp.first_name} is NOT qualified for ${waName}.\nAssign anyway?`;
        if (!window.confirm(confirmMsg)) return;
      }
    }

    const assignedObj = foundShift.assigned_employees.find((a: any) => a.id === empId);
    if (assignedObj) {
      try {
        await ShiftService.deleteShiftAssignment(assignedObj.assignment_id);
        await fetchTableShifts();
      } catch (error) {
        if (error instanceof HttpError) {
          if (!shiftErrorHandler.handle(error)) globalErrorHandler.handle(error);
        }
      }
    } else {
      if (isAutoAssign) {
        try {
          await ShiftService.createShiftAssignment({ shift: shiftId, employee: empId });
          await fetchTableShifts();
        } catch (error) {
          if (error instanceof HttpError) {
            if (!shiftErrorHandler.handle(error)) globalErrorHandler.handle(error);
          }
        }
      } else {
        await createAssignmentWithOverlapCheck({ shift: shiftId, employee: empId }, false);
      }
    }
  };

  const handleShiftClick = (shiftId: number) => {
    if (!activeEmployeeId) {
      console.warn('[ShiftClick] No employee selected in SHIFT-ESB');
      return;
    }
    handleToggleAssignment(shiftId, activeEmployeeId);
  };

  const handleQuickAdd = (dateStr: string, workAreaId: number) => {
    const workArea = workAreaOptions.find((wa: any) => wa.id === workAreaId);
    const workAreaIdStr = workArea ? String(workArea.id) : '';
    setShiftData({
      start_time_date: dateStr, start_time_time: '09:00', end_time_time: '17:00',
      employees_needed: 1, work_area: workAreaIdStr, notes: '',
      weekdays: [], interval: 1, repeat_until: '', never_ends: false,
      crm_task: null, crm_task_title: '',
    });
    setShowAddShift(true);
  };

  const handleEditClick = (shift: any) => {
    const sdt = dateUtil.convertUTCToTenantTime(shift.start_time) || new Date(shift.start_time);
    const edt = dateUtil.convertUTCToTenantTime(shift.end_time) || new Date(shift.end_time);
    setEditShiftData({
      shiftId: shift.id,
      startDate: `${sdt.getFullYear()}-${dateUtil.pad2(sdt.getMonth() + 1)}-${dateUtil.pad2(sdt.getDate())}`,
      startTime: `${dateUtil.pad2(sdt.getHours())}:${dateUtil.pad2(sdt.getMinutes())}`,
      endTime: `${dateUtil.pad2(edt.getHours())}:${dateUtil.pad2(edt.getMinutes())}`,
      employeesNeeded: shift.employees_needed, workArea: shift.work_area || '',
      notes: shift.notes || '', scope: 'this',
      crm_task: shift.crm_task || null, crm_task_title: shift.crm_task_title || '',
    });
    setShowEditShift(true);
  };

  const closeEditShiftModal = () => setShowEditShift(false);

  const handleEditShiftSave = async () => {
    const { shiftId, startDate: sd, startTime, endTime, employeesNeeded, workArea, notes, scope, crm_task } = editShiftData;
    let startUtc = dateUtil.createTenantTimeAsUTC(sd, startTime);
    let endUtc = dateUtil.createTenantTimeAsUTC(sd, endTime);
    if (!startUtc || !endUtc) { toast.warning('Invalid date/time'); return; }
    if (endUtc <= startUtc) {
      const nextDay = new Date(sd);
      nextDay.setDate(nextDay.getDate() + 1);
      const nextDayStr = `${nextDay.getFullYear()}-${String(nextDay.getMonth() + 1).padStart(2, '0')}-${String(nextDay.getDate()).padStart(2, '0')}`;
      endUtc = dateUtil.createTenantTimeAsUTC(nextDayStr, endTime) || endUtc;
    }
    try {
      await ShiftService.updateShift(shiftId, {
        start_time: startUtc, end_time: endUtc, employees_needed: employeesNeeded,
        work_area: workArea, notes: notes || '', crm_task: crm_task || null,
      }, scope);
      setShowEditShift(false);
      await fetchTableShifts();
    } catch (error) {
      if (error instanceof HttpError) {
        if (!shiftErrorHandler.handle(error)) globalErrorHandler.handle(error);
      }
    }
  };

  const handleEditShiftDelete = async () => {
    const { shiftId, scope } = editShiftData;
    try {
      const result = await ShiftService.deleteShift(shiftId, scope);
      setShowEditShift(false);
      await fetchTableShifts();
      const deletedIds: number[] = result?.deleted_ids || [shiftId];
      return { deletedIds, fetchTableShifts };
    } catch (error) {
      if (error instanceof HttpError) {
        if (!shiftErrorHandler.handle(error)) globalErrorHandler.handle(error);
      }
      return null;
    }
  };

  // ===== WA DnD reorder =====
  const handleWADragEnd = (result: any) => {
    if (!result.destination) return;
    const { source, destination } = result;
    if (source.index === destination.index) return;
    const newOrder = Array.from(draggableWAList);
    const [removed] = newOrder.splice(source.index, 1);
    newOrder.splice(destination.index, 0, removed);
    setDraggableWAList(newOrder);
    LocalStorageService.setWaOrder(newOrder.map((wa: any) => wa.id));
  };

  // ===== Shift DnD =====
  const handleShiftDragEnd = useCallback(async (result: any) => {
    const { destination, source, draggableId } = result || {};
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;
    const [, shiftIdStr] = String(draggableId).split('|');
    const [, destDate, destWaStr] = String(destination.droppableId).split('|');
    const shiftId = Number(shiftIdStr);
    const destWaId = Number(destWaStr);
    if (!shiftId || !destDate || Number.isNaN(destWaId)) return;
    let selected: any = null;
    outer: for (let dayObj of tableShifts as any[]) {
      for (let s of dayObj.shifts || []) {
        if (s.id === shiftId) { selected = s; break outer; }
      }
    }
    if (!selected) return;
    const sDT = dateUtil.convertUTCToTenantTime(selected.start_time) || new Date(selected.start_time);
    const eDT = dateUtil.convertUTCToTenantTime(selected.end_time) || new Date(selected.end_time);
    const pad = (n: number) => String(n).padStart(2, '0');
    const sTimeStr = `${pad(sDT.getHours())}:${pad(sDT.getMinutes())}`;
    const eTimeStr = `${pad(eDT.getHours())}:${pad(eDT.getMinutes())}`;
    let dndStartUtc = dateUtil.createTenantTimeAsUTC(destDate, sTimeStr);
    let dndEndUtc = dateUtil.createTenantTimeAsUTC(destDate, eTimeStr);
    if (dndStartUtc && dndEndUtc && dndEndUtc <= dndStartUtc) {
      const nextDay = new Date(destDate);
      nextDay.setDate(nextDay.getDate() + 1);
      const nextDayStr = `${nextDay.getFullYear()}-${String(nextDay.getMonth() + 1).padStart(2, '0')}-${String(nextDay.getDate()).padStart(2, '0')}`;
      dndEndUtc = dateUtil.createTenantTimeAsUTC(nextDayStr, eTimeStr) || dndEndUtc;
    }
    try {
      await ShiftService.updateShift(selected.id, {
        start_time: dndStartUtc || new Date().toISOString(),
        end_time: dndEndUtc || new Date().toISOString(),
        work_area: destWaId, employees_needed: selected.employees_needed || 1,
        notes: selected.notes || '',
      }, 'this');
      await fetchTableShifts();
    } catch (e) {
      console.error('Shift DnD move =>', e);
    }
  }, [tableShifts, fetchTableShifts]);

  // ===== Auto-assign & Clear All (delegated) =====
  const autoAssign = useAutoAssign({
    tableShifts, allEmployees,
    setShowAutoAssignConfirm, setIsAssigning, setAssignmentLogs, addLog,
    setShowClearAllModal, handleToggleAssignment, fetchTableShifts,
  });

  return {
    swapRequests,
    handleShiftClick,
    handleEditClick,
    handleLongPressStart,
    handleToggleAssignment,
    handleQuickAdd,
    closeEditShiftModal,
    handleEditShiftSave,
    handleEditShiftDelete,
    handleAutoAssignClick: autoAssign.handleAutoAssignClick,
    executeAutoAssign: autoAssign.executeAutoAssign,
    handleClearAllClick: autoAssign.handleClearAllClick,
    handleClearAllConfirm: autoAssign.handleClearAllConfirm,
    handleWADragEnd,
    handleShiftDragEnd,
  };
}
